/**
 * Demo Entry Point
 *
 * This file imports components and configures datasets.
 */

// Register all web components
import './components/schema-diagram';
import './components/dataset-selector';
import './components/find-node';
import './components/sparql-editor';
import './components/path-list';
import './components/node-tooltip';
import './components/iri-resolver';
import './components/diagram-settings';
import './components/results-panel';
import './components/shapes-panel';

// Apply default style
import { setStyle, DEFAULT_STYLE } from './layout/styles';

import type { DatasetSelector } from './components/dataset-selector';
import type { FindNode } from './components/find-node';
import type { SparqlEditor } from './components/sparql-editor';
import type { PathList } from './components/path-list';
import type { IriResolver } from './components/iri-resolver';
import type { ResultsPanel } from './components/results-panel';
import type { ShapesPanel } from './components/shapes-panel';

// =============================================================================
// DATASET CONFIGURATION - discovered at runtime from manifest.json
// =============================================================================

/** Deterministic colour from a string hash so every dataset gets a unique hue. */
function colourFor(id: string, index: number, total: number): string {
  const hue = Math.round((360 * index) / Math.max(total, 1));
  return `hsl(${hue},65%,50%)`;
}

interface ManifestEntry { id: string; file: string; name: string }

/**
 * Fetch manifest.json (generated by CI from all demo/*.jsonld files) and
 * build the DATASETS dict that the selector component expects.
 * Falls back to an empty dict if the manifest is not available (e.g. when
 * developing locally without having run the manifest generator).
 */
async function loadDatasets(): Promise<Record<string, { name: string; url: string; color: string }>> {
  try {
    const res = await fetch('./manifest.json');
    if (!res.ok) throw new Error(`manifest ${res.status}`);
    const manifest: ManifestEntry[] = await res.json();
    const datasets: Record<string, { name: string; url: string; color: string }> = {};
    manifest.forEach((entry, i) => {
      datasets[entry.id] = {
        name: entry.name,
        url: `./${entry.file}`,
        color: colourFor(entry.id, i, manifest.length),
      };
    });
    return datasets;
  } catch {
    console.warn('manifest.json not found – no datasets loaded. Run the CI build or create one manually.');
    return {};
  }
}

// =============================================================================
// INITIALIZATION - wire dataset config into the selector
// =============================================================================

document.addEventListener('DOMContentLoaded', async () => {
  setStyle(DEFAULT_STYLE);

  const DATASETS = await loadDatasets();

  // Feed datasets into the selector component
  const selector = document.querySelector<DatasetSelector>('dataset-selector');
  if (selector) selector.setDatasets(DATASETS);

  // When a schema is loaded, refresh dependent components
  const findNode = document.querySelector<FindNode>('find-node');
  const sparqlEditor = document.querySelector<SparqlEditor>('sparql-editor');
  const pathList = document.querySelector<PathList>('path-list');
  const iriResolver = document.querySelector<IriResolver>('iri-resolver');
  const resultsPanel = document.querySelector<ResultsPanel>('results-panel');
  const shapesPanel = document.querySelector<ShapesPanel>('shapes-panel');

  // Pre-fetch all JSON-LD files to discover SPARQL endpoints before any
  // diagram is rendered - the IRI Resolver endpoint list is populated
  // immediately so the user can start resolving IRIs right away.
  if (iriResolver) {
    iriResolver.loadEndpointsFromUrls(DATASETS);
  }

  selector?.addEventListener('schema-loaded', () => {
    findNode?.refresh();
    sparqlEditor?.refresh();
    pathList?.refresh();
    iriResolver?.refresh();
    resultsPanel?.refreshEndpoints();
    shapesPanel?.refresh();
  });

  // Status bar
  selector?.addEventListener('status', ((e: CustomEvent) => {
    const el = document.getElementById('status-text');
    if (el) el.textContent = e.detail;
  }) as EventListener);
});
